import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import type { UseHandleSendRequestProps } from './types'
import { ApiErrorCode } from '@/api/apiTypes'
import { CryptoCode, FiatCode } from '@/constants/currencyCodes'
import {
  useCreatePurchaseOnRamp,
  type CreatePurchaseOnRampPayload,
} from '@/domain/transfer'
import { useCreateRequest } from '@/domain/requests'
import { useCreateInternalTransfer } from '@/hooks/useCreateInternalTransfer'
import { useCheckTransactionEligibility } from '@/hooks/useCheckTransactionEligibility/useCheckTransactionEligibility'
import { useValidateFiatAmount } from '@/hooks/useValidateFiatAmount'
import { useGetWalletDataByCryptoCode } from '@/hooks/useGetWalletDataByCryptoCode'
import { useSendStore } from '@/store/useSendStore'
import { useUserStore } from '@/store/useUserStore'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'
import { useTransactionStore } from '@/store/useTransactionStore'
import { currencyUtils } from '@/utils'

const { formatFiatCurrency, parseAmountToFloat } = currencyUtils

/**
 * Hook for handling send/request money operations
 * Based on React Native useHandleSendRequest
 */
export const useHandleSendRequest = ({
  type,
  currencyValue,
  currencyCode,
  fiatAmount,
  fiatCode,
  amount,
  requestId,
  recipientPhone, // Phone from navigation state (fallback if store doesn't have it)
}: UseHandleSendRequestProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const isSend = type === 'send'

  // Get wallet data for the selected currency
  const walletData = useGetWalletDataByCryptoCode({ cryptoCode: currencyCode })

  // Store data - use direct selectors to avoid creating new objects
  const recipient = useSendStore((s) => s.recipient)
  const note = useSendStore((s) => s.note)
  const sendRequestForm = useSendStore((s) => s.sendRequestForm)
  const onRampTotalAmount = sendRequestForm?.onRampTotalAmount

  const paymentMethod = useSendStore((s) => s.paymentMethod)
  const displayCurrency = useAppPreferencesStore((s) => s.displayCurrency)
  const myId = useUserStore((s) => s.userAuthenticated?.user_id)
  const walletId = useUserStore((s) => s.wallet?.walletId)
  const setLastTransactionData = useTransactionStore(
    (s) => s.setLastTransactionData,
  )
  const resetSend = useSendStore((s) => s.resetSend)

  const isWallet = paymentMethod === 'Wallet'
  const hasEnoughBalance =
    Number(walletData?.amount || 0) >= Number(currencyValue || 0)

  // Navigation to success page
  const onNext = useCallback(() => {
    const relativeFiatFormatted = formatFiatCurrency({
      fiatCode,
      value: parseAmountToFloat({
        amount: fiatAmount || '0',
        decimalSeparator: '.',
      }),
      showDecimals: true,
    })

    const successParams = {
      title: isSend ? t('translation:youSent') : t('translation:youRequested'),
      description: `${isSend ? t('translation:to') : t('translation:from')} ${recipient?.name || ''}`,
      amount,
      code: currencyCode,
      label: walletData?.label,
      relativeFiatFormatted,
    }

    // Reset send store before navigation
    resetSend()

    navigate('/send-request/success', {
      state: successParams,
    })
  }, [
    fiatCode,
    fiatAmount,
    isSend,
    t,
    recipient?.name,
    amount,
    currencyCode,
    walletData?.label,
    resetSend,
    navigate,
  ])

  // Error handler
  const handleError = useCallback(
    (message: string, noNeedTranslate?: boolean) => {
      toast.error(noNeedTranslate ? message : t(message), {
        style: {
          width: 'calc(100vw - 26px)',
          marginLeft: '13px',
          marginRight: '13px',
          maxWidth: 'calc(100vw - 26px)',
        },
      })
    },
    [t],
  )

  // OnRamp redirect handler - redirects to WebView with payment_url or session token
  const handlePurchaseOnRampRedirect = useCallback(
    (orderData: {
      transaction_id: string
      order_id: string
      payment_url?: string // For hosted page redirect
      payment_session_token?: string // For Checkout.com Flow SDK
      payment_session_secret?: string // For Checkout.com Flow SDK
    }) => {
      // Check if we have payment_url OR payment_session_token
      if (!orderData.payment_url && !orderData.payment_session_token) {
        return handleError(
          'Payment initialization failed. Please try again.',
          true,
        )
      }

      setLastTransactionData({
        transactionId: orderData.transaction_id,
        paymentUrl: orderData.payment_url,
        paymentSessionToken: orderData.payment_session_token,
        paymentSessionSecret: orderData.payment_session_secret,
      })

      setTimeout(() => {
        navigate('/on-ramp-webview')
      }, 500)
    },
    [setLastTransactionData, navigate, handleError],
  )

  // Mutations
  const createRequestMutation = useCreateRequest()
  const createInternalTransferMutation = useCreateInternalTransfer()
  const { createPurchaseOnRamp, isPending: isLoadingCreatingPurchase } =
    useCreatePurchaseOnRamp({
      onSuccess: handlePurchaseOnRampRedirect,
      onError: ({ errorMessage, code }) => {
        if (code === ApiErrorCode.COUNTRY_RESTRICTED) {
          return navigate('/service-unavailable')
        }

        handleError(errorMessage as string, true)
      },
    })

  // Eligibility check
  const { checkTransactionEligibility } = useCheckTransactionEligibility({
    feature: 'on_ramp',
  })

  // Validation hooks
  const { validateFiatAmount } = useValidateFiatAmount({
    cryptoCode: walletData?.displayCurrencyCode as CryptoCode,
    min: {
      amount: String(walletData?.minForOnRamp?.fiat_amount || '1'),
      code: (walletData?.minForOnRamp?.fiat_currency as FiatCode) || 'USD',
    },
    max: {
      amount: String(walletData?.maxForOnRamp?.fiat_amount || '10000'),
      code: (walletData?.maxForOnRamp?.fiat_currency as FiatCode) || 'USD',
    },
  })

  const { validateFiatAmount: validateFiatAmountInWallet } =
    useValidateFiatAmount({
      cryptoCode: walletData?.displayCurrencyCode as CryptoCode,
      min: {
        amount: '0',
        code: walletData?.fiatCurrency as FiatCode,
      },
      max: {
        amount: String(walletData?.fiatAmount || '0'),
        code: walletData?.fiatCurrency as FiatCode,
      },
      customMaxMessage: 'insufficientBalance',
    })

  // Main handler function
  const handleSendRequest = useCallback(async () => {
    if (isSend) {
      // SEND MONEY LOGIC
      if (isWallet) {
        // Internal transfer from wallet
        if (!hasEnoughBalance) {
          return handleError('insufficientBalance')
        }

        const isValid = validateFiatAmountInWallet({
          fiatAmount: {
            amount: fiatAmount,
            code: displayCurrency?.isin as FiatCode,
          },
          isConfirmationScreen: true,
        })

        if (!isValid) {
          return
        }

        try {
          // Build payload, excluding empty phone number (server validation)
          const payload: any = {
            amount: Number(currencyValue),
            currency: currencyCode as CryptoCode,
            purpose: note || undefined,
            from_wallet_id: walletId || '',
            payment_request_id: requestId,
          }

          // Add either to_user_id or to_phone_number, not both
          // Only add phone if it's not empty string
          if (recipient?.id) {
            payload.to_user_id = recipient.id
          }

          if (recipient?.phone && recipient.phone.trim() !== '') {
            payload.to_phone_number = recipient.phone
          }

          await createInternalTransferMutation.mutateAsync(payload)

          onNext()
        } catch (error) {
            return handleError('errors.somethingWrong')
        }
      } else {
        // OnRamp purchase (Card payment)
        const isValid = validateFiatAmount({
          fiatAmount: {
            amount: fiatAmount,
            code: displayCurrency?.isin as FiatCode,
          },
          isConfirmationScreen: true,
        })

        if (!isValid) {
          return
        }

        const isEligible = await checkTransactionEligibility({
          amountInUSD: Number(fiatAmount || 0),
        })

        if (!isEligible || !paymentMethod) {
          return
        }

        // Use phone from store or fallback to recipientPhone parameter
        const phoneToUse =
          recipient?.phone &&
          recipient.phone !== 'null' &&
          recipient.phone.trim() !== ''
            ? recipient.phone
            : recipientPhone

        // Phone number is optional - backend will handle if it's required for specific scenarios
        const hasValidPhone = phoneToUse && phoneToUse.trim() !== ''

        // Prepare OnRamp payload
        // Note: For send-request, we use fiatAmount (not onRampTotalAmount)
        // Priority: recipientUserId > phoneNumber (like internal transfer)
        const onRampPayload: CreatePurchaseOnRampPayload = {
          userId: myId || '',
          recipientUserId: recipient?.id || undefined, // Preferred: recipient user ID
          phoneNumber: hasValidPhone ? phoneToUse : undefined, // Fallback: phone number
          amount: String(fiatAmount || '0'),
          fiatCurrency: (displayCurrency?.isin || 'USD') as any, // Can be any currency code
          cryptoCurrency: currencyCode,
          paymentMethod, // 'Card' | 'ApplePay' | 'GooglePay' | 'OnlineBanking'
          purpose: note || undefined,
          paymentRequestId: requestId,
        }

        await createPurchaseOnRamp(onRampPayload)
      }
    } else {
      // REQUEST MONEY LOGIC
      try {
        await createRequestMutation.mutateAsync({
          initiator_user_id: myId || '',
          acceptor_user_id: recipient?.id,
          acceptor_phone_number: recipient?.phone,
          request_type: 'send_request',
          extra_info: {
            amount: currencyValue,
            currency: currencyCode,
            note: note || '',
          },
        })

        onNext()
      } catch (error) {
        return handleError('errors.somethingWrong')
      }
    }
  }, [
    isSend,
    isWallet,
    hasEnoughBalance,
    handleError,
    validateFiatAmountInWallet,
    fiatAmount,
    displayCurrency?.isin,
    createInternalTransferMutation,
    currencyValue,
    currencyCode,
    recipient?.id,
    recipient?.phone,
    note,
    walletId,
    requestId,
    onNext,
    validateFiatAmount,
    checkTransactionEligibility,
    paymentMethod,
    onRampTotalAmount?.amount,
    onRampTotalAmount?.code,
    createPurchaseOnRamp,
    createRequestMutation,
    myId,
  ])

  return {
    handleSendRequest,
    isLoading:
      createRequestMutation.isPending ||
      createInternalTransferMutation.isPending ||
      isLoadingCreatingPurchase,
  }
}
